import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../partage/api/client';
import { cles } from '../../partage/relectures';
import type { PossibleDuJour, PreparationDuJour, ReglagesDeProposition } from '../../partage/api/types';
import {
  AJOUTER_UNE_TACHE,
  C_EST_MA_JOURNEE,
  CHANGER_MA_JOURNEE,
  DANS_TA_JOURNEE,
  FINIRAIT_APRES_MINUIT,
  RECOMMENCER,
  RECOMMENCER_NOTE,
  REPLIER_LA_LISTE,
  retirerDeLaJournee,
  RIEN_DE_PREVU_AUJOURDHUI,
  GESTES_DU_CHANGEMENT,
  TA_JOURNEE,
} from '../../partage/vocabulaire';
import { CreneauxDeLaJournee, CroixDuRetrait } from './CreneauxDeLaJournee';
import { dureeLisible } from './datesDeLAgenda';
import { faitesDeLaJournee } from './journeeAChanger';
import { choixAChanger } from './journeeDuMoment';
import { tempsDeLaJournee } from './journeeEnCours';
import { ListeAuChoix } from './ListeAuChoix';
import { JaugeDuJour, TempsPris } from './TempsDeLaJournee';
import { useJourneeAComposer } from './useJourneeAComposer';
import './LaQuestion.css';
import './PreparerUnJour.css';
import './ProposeMoi.css';
import './JeChoisis.css';
import './JourneeAuPouce.css';

/**
 * « Changer ma journée », au téléphone (chantier `telephone-journee-agenda`, ticket 04, décision
 * d'Igor du 2026-10-05, 8) : la journée telle qu'elle est, à modifier. × enlève, « + Ajouter une
 * tâche » ouvre la liste de « Je choisis » (ses trois boutons), toucher une heure la change ;
 * « Recommencer », en bas, repose la question.
 *
 * Ce qui est fait reste à sa place, « Faite », sans geste, et compte dans le temps pris, comme
 * dans le panneau « Préparer ma journée » ; ce qui ne se fait plus aujourd'hui part. Rien ne
 * s'écrit avant « C'est ma journée », qui enregistre avec les réglages de la journée (début,
 * temps, où, courage) ; « ‹ Ta journée » revient sans rien écrire.
 *
 * Depuis l'Agenda, « Changer ce jour » ouvre le même écran pour un autre jour (ticket 06) : son
 * retour, son titre et sa phrase quand la journée a disparu sont alors ceux de l'Agenda.
 */
export function ChangerMaJournee({
  jour,
  retour = TA_JOURNEE,
  titre = CHANGER_MA_JOURNEE,
  vide = RIEN_DE_PREVU_AUJOURDHUI,
  onRetour,
  onRecommencer,
  onFermer,
}: {
  jour: string;
  retour?: string;
  titre?: string;
  vide?: string;
  onRetour: () => void;
  onRecommencer: () => void;
  onFermer: (message: string) => void;
}) {
  // Une lecture fraîche : ce qui a été coché depuis la dernière ouverture compte.
  const preparation = useQuery({
    queryKey: cles.preparation(jour, 'changer'),
    queryFn: () => api.preparation(jour),
    staleTime: 0,
    gcTime: 0,
  });
  const donnees = preparation.data;

  const tete = (
    <>
      <button type="button" className="planifier-retour" onClick={onRetour}>
        ‹ {retour}
      </button>
      <h2 className="planifier-titre">{titre}</h2>
    </>
  );

  if (!donnees?.journee) {
    return (
      <section className="planifier-plein propose choisis changer" data-etape="changer" data-pret="non" aria-label={titre}>
        {tete}
        <p className="propose-note">{donnees ? vide : '…'}</p>
      </section>
    );
  }
  return (
    <LaJourneeAChanger donnees={{ ...donnees, journee: donnees.journee }} titre={titre} tete={tete} onRecommencer={onRecommencer} onFermer={onFermer} />
  );
}

function LaJourneeAChanger({
  donnees,
  titre,
  tete,
  onRecommencer,
  onFermer,
}: {
  donnees: PreparationDuJour & { journee: NonNullable<PreparationDuJour['journee']> };
  titre: string;
  tete: ReactNode;
  onRecommencer: () => void;
  onFermer: (message: string) => void;
}) {
  const enregistree = donnees.journee;
  const reglages: ReglagesDeProposition = {
    debut: enregistree.debut,
    tempsMin: enregistree.tempsMin,
    ou: enregistree.ou,
    courage: donnees.courage,
  };
  const { tempsMin } = reglages;
  const journee = useJourneeAComposer({
    jour: donnees.jour,
    reglages,
    onFermer,
    depart: choixAChanger(enregistree.taches, donnees.possibles),
    rangementFige: true,
  });
  const { liste } = journee;
  const [ajout, setAjout] = useState(false);
  // Le temps et le projet de chaque tâche : celles de la journée, puis celles ajoutées.
  const [connues, setConnues] = useState<Map<string, { minutes: number; projet: string | null }>>(
    () =>
      new Map(
        enregistree.taches.map((t) => [t.bulleId, { minutes: t.dureeMin, projet: t.dossier?.titre ?? null }] as [string, { minutes: number; projet: string | null }])
      )
  );
  const faites = faitesDeLaJournee(enregistree);

  const { utilise, reste } = tempsDeLaJournee(liste, [...connues].map(([id, t]) => [id, t.minutes] as const), tempsMin);

  function ajouterLaTache(p: PossibleDuJour) {
    if (journee.ajouterLaTache(p, reste)) {
      setConnues((avant) => (avant.has(p.id) ? avant : new Map(avant).set(p.id, { minutes: p.minutes, projet: p.dossier?.titre ?? null })));
    }
  }

  return (
    <section className="planifier-plein propose choisis changer" data-etape="changer" data-pret="oui" aria-label={titre}>
      {tete}

      <div className="choisis-journee" data-journee="oui">
        <div className="choisis-journee-tete">
          <span className="choisis-journee-nom">{DANS_TA_JOURNEE}</span>
          <span className="propose-compteur choisis-compteur">
            <TempsPris utilise={utilise} tempsMin={tempsMin} />
          </span>
        </div>
        <JaugeDuJour utilise={utilise} tempsMin={tempsMin} />
        {liste.length > 0 && journee.creneaux.length === 0 && <p className="propose-note">…</p>}
        <CreneauxDeLaJournee
          creneaux={journee.creneaux}
          faites={faites}
          enHeure={journee.enHeure}
          onOuvrirLHeure={(id) => {
            journee.setMessage(null);
            journee.setEnHeure(id);
          }}
          onQuitterLHeure={() => journee.setEnHeure(null)}
          onFixer={(id, h) => void journee.fixerLHeure(id, h)}
          quoi={(c) => [dureeLisible(c.minutes), connues.get(c.id)?.projet].filter(Boolean).join(' · ')}
          gestes={(c) => (
            <button
              type="button"
              className="preparer-geste propose-geste"
              aria-label={retirerDeLaJournee(c.titre)}
              data-geste="retirer"
              onClick={() => journee.retirerLaTache(c.id)}
            >
              <CroixDuRetrait />
            </button>
          )}
        />
        {liste.length > faites.size && <p className="propose-note">{GESTES_DU_CHANGEMENT}</p>}
      </div>

      <button type="button" className="propose-plus" aria-expanded={ajout} data-geste="ajouter" onClick={() => setAjout(!ajout)}>
        {ajout ? REPLIER_LA_LISTE : `${AJOUTER_UNE_TACHE} ›`}
      </button>
      {ajout && (
        <ListeAuChoix
          jour={donnees.jour}
          ou={reglages.ou}
          courage={reglages.courage}
          pris={liste.map((c) => c.id)}
          reste={reste}
          onAjouter={ajouterLaTache}
        />
      )}

      <footer className="propose-pied">
        {journee.message && (
          <p className="propose-message" role="status">
            {journee.message}
          </p>
        )}
        {journee.apresMinuit && (
          <p className="propose-message" role="status">
            {FINIRAIT_APRES_MINUIT}
          </p>
        )}
        <button type="button" className="preparer-fort" disabled={!journee.pret} onClick={() => journee.enregistrer.mutate()}>
          {C_EST_MA_JOURNEE}
        </button>
        <button type="button" className="changer-recommencer" data-geste="recommencer" onClick={onRecommencer}>
          {RECOMMENCER}
          <small>{RECOMMENCER_NOTE}</small>
        </button>
      </footer>
    </section>
  );
}
